// @flow

import * as React from 'react';
import ScrollContext from 'containers/ScrollContext';
import ViewportContext from 'containers/ViewportContext';
import { isDefined } from 'utils/functional';
import { bp } from '../theme';

const { useState, useContext, useMemo, useEffect, useRef } = React;

type Props = {
  children: ({
    isAboveViewport: boolean,
    isFullyAboveViewport: boolean,
    isAboveOffset: boolean,
    isInViewport: boolean
  }) => React.Node,
  offset?: Array<number>
};

const ScrollWatcherContainer = ({ children, offset }: Props) => {
  const [, scrollY] = useContext(ScrollContext);
  const [viewportWidth, viewportHeight] = useContext(ViewportContext);

  const [isAboveViewport, setIsAboveViewport] = useState(false);
  const [isFullyAboveViewport, setIsFullyAboveViewport] = useState(false);
  const [isAboveOffset, setIsAboveOffset] = useState(false);
  const [isInViewport, setIsInViewport] = useState(false);
  const [height, setHeight] = useState(0);
  const [threshold, setThreshold] = useState(null);

  const watchedElement = useRef(null);

  const currentBreakpoint = useMemo(
    () => {
      if (offset) {
        const breakpoint = bp.reduce(
          (acc, point, index) =>
            viewportWidth >= point && offset[index] ? index : acc,
          0
        );
        return offset[breakpoint];
      }
      return 0;
    },
    [offset, viewportWidth]
  );

  useEffect(
    () => {
      const thresholdValue: number = isDefined(threshold)
        ? threshold
        : watchedElement && watchedElement.current
        ? watchedElement.current.getBoundingClientRect().top
        : 0;
      const newIsAboveViewport = scrollY > thresholdValue;
      const newIsFullyAboveViewport =
        scrollY > thresholdValue + height - currentBreakpoint;
      const newIsAboveOffset = offset
        ? scrollY > thresholdValue - currentBreakpoint
        : false;
      const newIsInViewport =
        window.innerHeight + scrollY >
        height + thresholdValue - currentBreakpoint;
      setIsFullyAboveViewport(newIsFullyAboveViewport);
      setIsAboveViewport(newIsAboveViewport);
      setIsAboveOffset(newIsAboveOffset);
      setIsInViewport(newIsInViewport);
    },
    [currentBreakpoint, height, offset, scrollY, threshold]
  );

  useEffect(
    () => {
      if (watchedElement && watchedElement.current) {
        const {
          height: newHeight,
          top
        } = watchedElement.current.getBoundingClientRect();
        setHeight(newHeight);
        setThreshold(top + scrollY);
        const count = 0;
        const timer = () => {
          window.setTimeout(() => {
            if (height > 0 || count >= 10) {
              return;
            }
            timer();
          }, 50);
        };
        timer();
      }
    },
    [height, scrollY, viewportHeight, viewportWidth]
  );

  return (
    <div ref={watchedElement}>
      {children({
        isAboveViewport,
        isFullyAboveViewport,
        isAboveOffset,
        isInViewport
      })}
    </div>
  );
};

export default ScrollWatcherContainer;
